import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Sun, Shield, Sparkles, Eye, Thermometer } from "lucide-react";
import heroWindowTint from "@/assets/hero-window-tint.webp";
import TintSimulator from "@/components/TintSimulator";

const WindowTint = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Automotive Window Tinting Las Vegas | Heat Rejection Tint</title>
        <meta name="description" content="Premium XPEL Prime window tinting in Las Vegas. Up to 98% heat rejection with ceramic films. Automotive & residential tint. UV protection & privacy. Free quote." />
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={heroWindowTint} 
            alt="XPEL Window Tint Las Vegas - Ceramic Film" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/85 to-black/75" />
        </div>
        
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-3xl">
            <Sun className="w-16 h-16 text-accent mb-6" />
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">
              Window Tint
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Premium window films for style, privacy, and comprehensive UV protection
            </p>
            <Button asChild size="lg" variant="premium">
              <Link to="/contact">Request Free Quote</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-12 text-center uppercase">
              Premium XPEL Window Tinting Benefits
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">UV Protection</h3>
                  <p className="text-muted-foreground">
                    Blocks 99% of harmful UV rays, protecting you and your vehicle's interior
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Heat Rejection</h3>
                  <p className="text-muted-foreground">
                    Ceramic films reduce interior temperature by up to 60% for enhanced comfort
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Enhanced Privacy</h3>
                  <p className="text-muted-foreground">
                    Various tint levels provide the privacy you desire while maintaining visibility
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Glare Reduction</h3>
                  <p className="text-muted-foreground">
                    Minimizes eye strain and improves driving safety in bright conditions
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Interior Protection</h3>
                  <p className="text-muted-foreground">
                    Prevents fading and cracking of dashboard, seats, and other interior components
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Lifetime Warranty</h3>
                  <p className="text-muted-foreground">
                    Premium films backed by manufacturer lifetime warranty against fading and peeling
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-6 text-center uppercase">
              See XPEL Window Tint In Action
            </h2>
            <p className="text-center text-muted-foreground mb-8">
              Watch how our ceramic window films provide superior heat rejection and UV protection
            </p>
            <div className="relative aspect-video rounded-lg overflow-hidden border border-border">
              <video
                className="w-full h-full object-cover"
                controls
                autoPlay
                muted
                loop
                playsInline
              >
                <source 
                  src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/videos/1763210084306.mp4`}
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Film Options Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Film Options */}
            <Card className="bg-card border-silver mb-12">
              <CardContent className="p-8">
                <h3 className="text-2xl font-heading font-bold mb-6 uppercase">XPEL PRIME Film Options</h3>
                <div className="space-y-4">
                  <div className="py-4 border-b border-border">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-semibold text-lg">XPEL PRIME CS</span>
                      <span className="text-accent font-semibold">Color Stable</span>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      Quality protection with reliable UV blocking and long-term color stability. Won't fade or turn purple.
                    </p>
                  </div>
                  
                  <div className="py-4 border-b border-border">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-semibold text-lg">XPEL PRIME XR</span>
                      <span className="text-accent font-semibold">Ceramic</span>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      Ceramic-based heat rejection technology. Non-metallic construction ensures no signal interference.
                    </p>
                  </div>
                  
                  <div className="py-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-semibold text-lg">XPEL PRIME XR PLUS</span>
                      <span className="text-accent font-semibold">Multi-Layer Nano-Ceramic</span>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      Premium multi-layer nano-ceramic film with maximum heat rejection. Industry-leading IR blocking technology.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tint Simulator */}
            <TintSimulator />

            {/* Custom Wraps */}
            <div className="bg-card border border-border rounded-lg p-8">
              <h3 className="text-2xl font-heading font-bold mb-4">Custom Vehicle Wraps</h3>
              <p className="text-muted-foreground mb-6">
                Transform your vehicle's appearance with premium vinyl wraps. Choose from thousands of colors, finishes, and textures including matte, satin, gloss, chrome, and textured options.
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                  <span>Full vehicle color changes</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                  <span>Accent wraps (roof, hood, mirrors)</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                  <span>Commercial vehicle graphics</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                  <span>Reversible and paint-safe</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

    {/* Why XR Plus Section */}
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Thermometer className="w-16 h-16 text-accent mx-auto mb-6" />
            <h2 className="text-4xl font-heading font-bold mb-6 uppercase">
              Why XPEL PRIME XR PLUS?
            </h2>
            <p className="text-xl text-muted-foreground">
              Experience the ultimate in heat rejection technology
            </p>
          </div>

          <Card className="bg-card border-accent shadow-[var(--shadow-gold-glow)]">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-3xl font-heading font-bold text-accent mb-4">
                    Blocks up to 98% of Infrared Heat
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    You can feel the difference. XPEL PRIME XR PLUS keeps your cabin significantly 
                    cooler than standard tint, reducing AC usage and improving comfort in the Las Vegas summer.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold">Superior IR Rejection</span>
                        <p className="text-sm text-muted-foreground">Multi-layer nano-ceramic technology blocks heat without affecting visibility</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold">Reduce Energy Costs</span>
                        <p className="text-sm text-muted-foreground">Less AC usage means better fuel economy and reduced wear on your climate system</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold">Enhanced Comfort</span>
                        <p className="text-sm text-muted-foreground">Drive in Las Vegas heat without feeling like you're in an oven</p>
                      </div>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-background/50 rounded-lg p-6">
                  <h4 className="text-xl font-heading font-bold mb-4 uppercase text-center">Heat Rejection Comparison</h4>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm">XPEL PRIME CS</span>
                        <span className="text-accent font-semibold">40%</span>
                      </div>
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-accent/50" style={{ width: '40%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm">XPEL PRIME XR</span>
                        <span className="text-accent font-semibold">88%</span>
                      </div>
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-accent/70" style={{ width: '88%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm font-semibold">XPEL PRIME XR PLUS</span>
                        <span className="text-accent font-semibold">98%</span>
                      </div>
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-accent" style={{ width: '98%' }}></div>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground text-center mt-4">
                    *IR heat rejection at 900-1000nm wavelength
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>

    {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-accent/20 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
            Enhance Comfort & Style
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Get a custom quote for window tint or vehicle wraps
          </p>
          <Button asChild variant="hero" size="lg">
            <Link to="/contact">Request Free Quote</Link>
          </Button>
        </div>
    </section>

    {/* Residential & Commercial Tint Section */}
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
              Residential & Commercial Tint
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Protect your home or office with the same high-performance solar control and privacy films we trust for vehicles. Our residential and commercial films reduce energy costs, block 99% of harmful UV rays, and enhance the comfort and security of your space.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <Sparkles className="w-12 h-12 text-accent mb-4" />
                <h3 className="text-xl font-heading font-bold mb-3">Reduce Energy Costs</h3>
                <p className="text-muted-foreground">
                  Solar control films dramatically reduce heat gain, lowering cooling costs by up to 30%
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <Shield className="w-12 h-12 text-accent mb-4" />
                <h3 className="text-xl font-heading font-bold mb-3">Block 99% UV Rays</h3>
                <p className="text-muted-foreground">
                  Protect furniture, flooring, and artwork from fading and sun damage
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <Eye className="w-12 h-12 text-accent mb-4" />
                <h3 className="text-xl font-heading font-bold mb-3">Enhanced Privacy</h3>
                <p className="text-muted-foreground">
                  Multiple tint levels available for desired privacy without sacrificing natural light
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <Sun className="w-12 h-12 text-accent mb-4" />
                <h3 className="text-xl font-heading font-bold mb-3">Glare Reduction</h3>
                <p className="text-muted-foreground">
                  Improve comfort and productivity by eliminating harsh glare on screens and surfaces
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <Shield className="w-12 h-12 text-accent mb-4" />
                <h3 className="text-xl font-heading font-bold mb-3">Interior Protection</h3>
                <p className="text-muted-foreground">
                  Preserve the appearance and value of your interior furnishings and finishes
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <Shield className="w-12 h-12 text-accent mb-4" />
                <h3 className="text-xl font-heading font-bold mb-3">Security Enhancement</h3>
                <p className="text-muted-foreground">
                  Safety films hold shattered glass together, providing added security and storm protection
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-card border-accent/50 mt-12">
            <CardContent className="p-8">
              <h3 className="text-2xl font-heading font-bold mb-4">Perfect for Homes & Businesses</h3>
              <p className="text-muted-foreground mb-6">
                Whether you need to reduce heat in a sun-exposed office, add privacy to conference rooms, or protect your home's interior from fading, our professional-grade window films deliver lasting results. We serve residential homes, commercial offices, retail spaces, and more throughout Las Vegas.
              </p>
              <Button asChild variant="premium">
                <Link to="/contact">Request Residential/Commercial Quote</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>

    {/* Schema Markup */}
    <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "XPEL PRIME Window Tinting and Vehicle Wraps",
          "provider": {
            "@type": "AutomotiveBusiness",
            "name": "Battle Born Protection Films"
          },
          "areaServed": {
            "@type": "City",
            "name": "Las Vegas"
          },
          "description": "Professional XPEL PRIME window tinting (CS, XR, XR PLUS) and custom vehicle wrap installation providing up to 98% IR heat rejection, UV protection, and enhanced style."
        })}
      </script>
    </div>
  );
};

export default WindowTint;
